/**
 * Centralized Repair Category Taxonomy and Deduplication Engine
 * Single source of truth for SmartFix repair categories, icons, and aliases.
 */

export const CANONICAL_REPAIR_CATEGORIES = [
  // --- Frequently Used / Top Priority (Rank 1 - 7) ---
  {
    key: 'air_conditioning',
    icon: 'cil-snowflake',
    th: 'เครื่องปรับอากาศและระบบเครื่องกล',
    en: 'Air Conditioning & Mechanical Systems',
    keywords: ['ปรับอากาศ', 'แอร์', 'เครื่องปรับอากาศ', 'เครื่องทำน้ำเย็น', 'air conditioning', 'air condition', 'hvac', 'chiller', 'cooling'],
    isPopular: true,
    rank: 1,
    preferredSectionCodes: ['BUILDING', 'ASSET']
  },
  {
    key: 'electricity_lighting',
    icon: 'cil-lightbulb',
    th: 'ไฟฟ้าและแสงสว่าง',
    en: 'Electricity & Lighting',
    keywords: ['ไฟฟ้า', 'หลอดไฟ', 'แสงสว่าง', 'ปลั๊กไฟ', 'ปลั๊ก', 'สวิตช์', 'ตรวจสอบระบบไฟฟ้า', 'ซ่อมบำรุงระบบไฟฟ้า', 'electricity', 'lighting', 'light', 'lamp', 'bulb', 'power', 'electrical'],
    isPopular: true,
    rank: 2,
    preferredSectionCodes: ['BUILDING', 'ASSET']
  },
  {
    key: 'plumbing_sanitation',
    icon: 'cil-drop',
    th: 'ประปาและสุขาภิบาล',
    en: 'Plumbing & Sanitation',
    keywords: ['ประปา', 'สุขาภิบาล', 'น้ำ', 'ท่อ', 'ก๊อกน้ำ', 'ก๊อก', 'ท่อน้ำรั่ว', 'ท่อน้ำ', 'น้ำไม่ไหล', 'ท่อแตก', 'ก๊อกน้ำรั่ว', 'ชักโครก', 'ซ่อมบำรุงระบบประปา', 'plumbing', 'sanitation', 'pipe', 'leak', 'water', 'tap', 'toilet', 'drain'],
    isPopular: true,
    rank: 3,
    preferredSectionCodes: ['BUILDING', 'ASSET']
  },
  {
    key: 'internet_network',
    icon: 'cil-wifi-signal-1',
    th: 'อินเทอร์เน็ตและเครือข่าย',
    en: 'Internet & Network',
    keywords: ['อินเทอร์เน็ต', 'เครือข่าย', 'wifi', 'wi-fi', 'lan', 'internet', 'network', 'เน็ต'],
    isPopular: true,
    rank: 4,
    preferredSectionCodes: ['CDTI']
  },
  {
    key: 'computers_equipment',
    icon: 'cil-monitor',
    th: 'คอมพิวเตอร์และอุปกรณ์',
    en: 'Computers & Equipment',
    keywords: ['คอมพิวเตอร์', 'เครื่องคอม', 'คอม', 'pc', 'computer', 'monitor', 'จอภาพ', 'laptop'],
    isPopular: true,
    rank: 5,
    preferredSectionCodes: ['CDTI']
  },
  {
    key: 'printers_scanners',
    icon: 'cil-print',
    th: 'เครื่องพิมพ์และสแกนเนอร์',
    en: 'Printers & Scanners',
    keywords: ['เครื่องพิมพ์', 'สแกนเนอร์', 'ปริ้น', 'พิมพ์', 'printer', 'scanner', 'print'],
    isPopular: true,
    rank: 6,
    preferredSectionCodes: ['CDTI']
  },
  {
    key: 'elevators_doors',
    icon: 'cil-door',
    th: 'ลิฟต์และประตูอัตโนมัติ',
    en: 'Elevators & Automatic Doors',
    keywords: ['ลิฟต์', 'ลิฟท์', 'ประตู', 'ประตูอัตโนมัติ', 'ลูกบิด', 'กลอน', 'elevator', 'door', 'automatic door', 'lock'],
    isPopular: true,
    rank: 7,
    preferredSectionCodes: ['BUILDING', 'ASSET']
  },

  // --- Complete Deduplicated Categories (Rank 8+) ---
  {
    key: 'buildings_civil',
    icon: 'cil-building',
    th: 'อาคารและงานโยธา',
    en: 'Buildings & Civil Works',
    keywords: ['อาคาร', 'โยธา', 'สถาปัตย์', 'ผนัง', 'พื้น', 'เพดาน', 'กระจก', 'หลังคา', 'แตกร้าว', 'ชำรุด', 'building', 'civil', 'architecture', 'wall', 'floor', 'roof', 'window'],
    isPopular: false,
    rank: 8,
    preferredSectionCodes: ['BUILDING', 'ASSET']
  },
  {
    key: 'furniture_equipment',
    icon: 'cil-couch',
    th: 'เฟอร์นิเจอร์และครุภัณฑ์ทั่วไป',
    en: 'Furniture & General Equipment',
    keywords: ['เฟอร์นิเจอร์', 'ครุภัณฑ์', 'โต๊ะ', 'เก้าอี้', 'ตู้', 'เตียง', 'furniture', 'general equipment', 'desk', 'chair', 'cabinet'],
    isPopular: false,
    rank: 9,
    preferredSectionCodes: ['BUILDING', 'ASSET']
  },
  {
    key: 'telephone_internal',
    icon: 'cil-phone',
    th: 'โทรศัพท์ภายใน',
    en: 'Internal Telephone',
    keywords: ['โทรศัพท์', 'telephone', 'phone', 'intercom'],
    isPopular: false,
    rank: 10,
    preferredSectionCodes: ['BUILDING', 'ASSET']
  },
  {
    key: 'cctv_communication',
    icon: 'cil-camera',
    th: 'กล้องวงจรปิดและระบบสื่อสาร',
    en: 'CCTV & Communication Systems',
    keywords: ['กล้องวงจรปิด', 'กล้อง', 'วงจรปิด', 'cctv', 'surveillance', 'communication system', 'ระบบสื่อสาร'],
    isPopular: false,
    rank: 11,
    preferredSectionCodes: ['CDTI', 'ASSET']
  },
  {
    key: 'cable_tv',
    icon: 'cil-tv',
    th: 'เคเบิลทีวี (Cable TV)',
    en: 'Cable TV',
    keywords: ['cable tv', 'cabletv', 'cable', 'เคเบิล', 'เคเบิ้ล', 'เคเบิลทีวี', 'ทีวี', 'tv', 'television'],
    isPopular: false,
    rank: 12,
    preferredSectionCodes: ['CDTI']
  },
  {
    key: 'av_meeting_rooms',
    icon: 'cil-screen-desktop',
    th: 'โสตทัศนูปกรณ์และห้องประชุม',
    en: 'Audio-Visual Equipment & Meeting Rooms',
    keywords: ['โสตทัศนูปกรณ์', 'โสต', 'ห้องประชุม', 'ไมค์', 'โปรเจคเตอร์', 'audio-visual', 'av', 'meeting room', 'projector', 'microphone'],
    isPopular: false,
    rank: 12,
    preferredSectionCodes: ['CAS']
  },
  {
    key: 'classroom_online_teaching',
    icon: 'cil-school',
    th: 'อุปกรณ์ห้องเรียนและการสอนออนไลน์',
    en: 'Classroom Equipment & Online Teaching Systems',
    keywords: ['อุปกรณ์ห้องเรียน', 'ห้องเรียน', 'สอนออนไลน์', 'smart classroom', 'online teaching', 'classroom equipment'],
    isPopular: false,
    rank: 13,
    preferredSectionCodes: ['CAS']
  },
  {
    key: 'user_accounts_email',
    icon: 'cil-user',
    th: 'บัญชีผู้ใช้ อีเมล และ Google Workspace',
    en: 'User Accounts, Email & Google Workspace',
    keywords: ['บัญชีผู้ใช้', 'อีเมล', 'google workspace', 'email', 'user account', 'password'],
    isPopular: false,
    rank: 14,
    preferredSectionCodes: ['CDTI']
  },
  {
    key: 'information_systems',
    icon: 'cil-code',
    th: 'ระบบสารสนเทศและซอฟต์แวร์',
    en: 'Information Systems & Software',
    keywords: ['ระบบสารสนเทศ', 'ซอฟต์แวร์', 'โปรแกรม', 'software', 'information system', 'mis', 'erp'],
    isPopular: false,
    rank: 15,
    preferredSectionCodes: ['CDTI']
  },
  {
    key: 'cleaning_environment',
    icon: 'cil-trash',
    th: 'ความสะอาดและสิ่งแวดล้อม',
    en: 'Cleaning & Environment',
    keywords: ['ความสะอาด', 'สิ่งแวดล้อม', 'ขยะ', 'แม่บ้าน', 'cleaning', 'environment', 'waste', 'trash'],
    isPopular: false,
    rank: 16,
    preferredSectionCodes: ['BUILDING']
  },
  {
    key: 'safety_fire_protection',
    icon: 'cil-shield-alt',
    th: 'ความปลอดภัยและอัคคีภัย',
    en: 'Safety & Fire Protection',
    keywords: ['ความปลอดภัย', 'อัคคีภัย', 'ดับเพลิง', 'ถังดับเพลิง', 'safety', 'fire protection', 'fire extinguisher', 'smoke'],
    isPopular: false,
    rank: 17,
    preferredSectionCodes: ['BUILDING']
  },
  {
    key: 'outdoor_landscape',
    icon: 'cil-spa',
    th: 'พื้นที่ภายนอกและภูมิทัศน์',
    en: 'Outdoor Area & Landscape',
    keywords: ['พื้นที่ภายนอก', 'ภูมิทัศน์', 'สนามหญ้า', 'ต้นไม้', 'outdoor area', 'landscape', 'garden', 'grounds'],
    isPopular: false,
    rank: 18,
    preferredSectionCodes: ['BUILDING']
  },
  {
    key: 'other',
    icon: 'cil-pencil',
    th: 'อื่น ๆ',
    en: 'Others',
    keywords: ['อื่น ๆ', 'อื่นๆ', 'other', 'others'],
    isPopular: false,
    rank: 99,
    preferredSectionCodes: ['OTHER']
  }
];

/**
 * Consolidates raw subcategories across sections and departments into a clean,
 * deduplicated list of unique repair categories while preserving all underlying
 * IDs and selection data for backend compatibility.
 *
 * @param {Array} rawOptions - Flat list of options from maintenanceGroups
 * @param {string} lang - 'th' or 'en'
 * @returns {Array} Deduplicated list of category objects
 */
export function deduplicateCategoryOptions(rawOptions, lang = 'th') {
  if (!Array.isArray(rawOptions) || rawOptions.length === 0) return [];

  const canonicalMap = new Map();
  CANONICAL_REPAIR_CATEGORIES.forEach(cat => {
    canonicalMap.set(cat.key, {
      ...cat,
      matchedOptions: []
    });
  });

  const unmatchedOptions = [];

  for (const opt of rawOptions) {
    if (!opt) continue;
    const previousNamesText = Array.isArray(opt.previousNames) ? opt.previousNames.join(' ') : '';
    const testText = `${opt.label || ''} ${opt.name || ''} ${previousNamesText}`.toLowerCase().trim();

    // Direct match for other
    if (opt.value === 'other' || opt.categoryId === 'other') {
      canonicalMap.get('other').matchedOptions.push(opt);
      continue;
    }

    let matchedKey = null;
    for (const cat of CANONICAL_REPAIR_CATEGORIES) {
      if (cat.key === 'other') continue;
      const hasKeyword = cat.keywords.some(kw => testText.includes(kw.toLowerCase()));
      if (hasKeyword) {
        matchedKey = cat.key;
        break;
      }
    }

    if (matchedKey) {
      canonicalMap.get(matchedKey).matchedOptions.push(opt);
    } else {
      unmatchedOptions.push(opt);
    }
  }

  const deduplicated = [];

  for (const cat of CANONICAL_REPAIR_CATEGORIES) {
    const entry = canonicalMap.get(cat.key);
    if (!entry || entry.matchedOptions.length === 0) continue;

    // Pick best option: prefer official standard section code (e.g. BUILDING, CDTI)
    let bestOption = entry.matchedOptions[0];
    if (Array.isArray(cat.preferredSectionCodes) && cat.preferredSectionCodes.length > 0) {
      const preferred = entry.matchedOptions.find(o =>
        cat.preferredSectionCodes.includes((o.categoryCode || '').toUpperCase())
      );
      if (preferred) bestOption = preferred;
    }

    const representedValues = new Set();
    const representedSubCategoryIds = new Set();
    entry.matchedOptions.forEach(o => {
      if (o.value) representedValues.add(String(o.value));
      if (o.subCategoryId) representedSubCategoryIds.add(String(o.subCategoryId));
      if (o.name) representedValues.add(String(o.name));
    });

    const localizedLabel = lang === 'en' ? cat.en : cat.th;

    deduplicated.push({
      canonicalKey: cat.key,
      icon: cat.icon,
      label: localizedLabel,
      name: localizedLabel,
      rawLabel: bestOption.label || localizedLabel,
      isPopular: Boolean(cat.isPopular),
      rank: cat.rank,
      keywords: cat.keywords || [],
      underlyingOption: bestOption,
      value: bestOption.value,
      subCategoryId: bestOption.subCategoryId,
      categoryId: bestOption.categoryId,
      categoryName: bestOption.categoryName,
      categoryCode: bestOption.categoryCode,
      representedValues,
      representedSubCategoryIds
    });
  }

  // Preserve any unmatched legitimate categories from database
  const unmatchedMap = new Map();
  for (const opt of unmatchedOptions) {
    const normName = (opt.name || opt.label || '').trim().toLowerCase();
    if (!normName) continue;
    if (!unmatchedMap.has(normName)) {
      unmatchedMap.set(normName, []);
    }
    unmatchedMap.get(normName).push(opt);
  }

  for (const [normName, opts] of unmatchedMap.entries()) {
    const primaryOpt = opts[0];
    const representedValues = new Set();
    const representedSubCategoryIds = new Set();
    opts.forEach(o => {
      if (o.value) representedValues.add(String(o.value));
      if (o.subCategoryId) representedSubCategoryIds.add(String(o.subCategoryId));
      if (o.name) representedValues.add(String(o.name));
    });

    let iconVal = primaryOpt.icon;
    if (!iconVal || (!iconVal.startsWith('/') && !iconVal.startsWith('http') && !/^(cil-|cib-|cif-)/.test(iconVal))) {
      iconVal = 'cil-settings';
    } else if (iconVal === 'cil-wrench') {
      iconVal = 'cil-settings';
    }

    deduplicated.push({
      canonicalKey: `custom_${primaryOpt.value || primaryOpt.subCategoryId}`,
      icon: iconVal,
      label: primaryOpt.label || primaryOpt.name,
      name: primaryOpt.name || primaryOpt.label,
      rawLabel: primaryOpt.label,
      isPopular: false,
      rank: 50,
      keywords: [primaryOpt.name || '', primaryOpt.label || '', normName],
      underlyingOption: primaryOpt,
      value: primaryOpt.value,
      subCategoryId: primaryOpt.subCategoryId,
      categoryId: primaryOpt.categoryId,
      categoryName: primaryOpt.categoryName,
      categoryCode: primaryOpt.categoryCode,
      representedValues,
      representedSubCategoryIds
    });
  }

  // Order by rank, ensuring 'other' remains at the end
  deduplicated.sort((a, b) => {
    if (a.canonicalKey === 'other') return 1;
    if (b.canonicalKey === 'other') return -1;
    return a.rank - b.rank;
  });

  return deduplicated;
}

/**
 * Checks whether a deduplicated category is currently selected in formData.
 * Synchronizes selected state across both Frequently Used and All Categories.
 *
 * @param {Object} categoryItem - Category item from deduplicateCategoryOptions
 * @param {Object|null} selectedSubCategory - formData.selectedSubCategory
 * @returns {boolean}
 */
export function isCategoryActive(categoryItem, selectedSubCategory) {
  if (!categoryItem || !selectedSubCategory) return false;
  const selVal = selectedSubCategory.value != null ? String(selectedSubCategory.value) : '';
  const selSubId = selectedSubCategory.subCategoryId != null ? String(selectedSubCategory.subCategoryId) : '';
  const selName = selectedSubCategory.name != null ? String(selectedSubCategory.name) : '';

  if (categoryItem.representedValues && (categoryItem.representedValues.has(selVal) || categoryItem.representedValues.has(selName))) {
    return true;
  }
  if (categoryItem.representedSubCategoryIds && selSubId && categoryItem.representedSubCategoryIds.has(selSubId)) {
    return true;
  }
  if (categoryItem.value === selVal || categoryItem.subCategoryId === selSubId) {
    return true;
  }
  return false;
}

/**
 * Filters categories by query string (case-insensitive substring match).
 *
 * @param {Array} categories
 * @param {string} query
 * @returns {Array}
 */
export function filterCategoriesByQuery(categories, query) {
  if (!Array.isArray(categories)) return [];
  if (!query || typeof query !== 'string' || !query.trim()) return categories;
  const q = query.trim().toLowerCase();
  return categories.filter(c => {
    const kwText = Array.isArray(c.keywords) ? c.keywords.join(' ') : '';
    const text = `${c.label || ''} ${c.name || ''} ${c.rawLabel || ''} ${c.categoryName || ''} ${kwText}`.toLowerCase();
    return text.includes(q);
  });
}
